import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { SEOHead } from "../components/SEOHead";
import { STATIC_PAGES } from "../seo/pages";
import { PublicHeader } from "../components/PublicHeader";
import { authFetch, getApiBase, getPathPrefix, resolveMediaUrl } from "../../utils/preview";
import "../styles/PublicCommon.css";
import "./HomePage.css";

// 施工実績カテゴリ・実績データの型定義
interface ResultCategory {
  id: string;
  name: string;
  description: string;
  display_order: number;
  thumbnail_url: string;
  is_enabled: boolean;
}

interface ResultMedia {
  storage_path: string;
  media_type?: string;
}

interface Result {
  id: string;
  title: string;
  category_id: string;
  completion_date: string;
  main_image: ResultMedia | null;
  location: string;
  is_published: boolean;
}

// お知らせメディアの型定義
interface AnnounceMedia {
  storage_path: string;
  media_type: string;
  caption?: string;
}

// お知らせデータの型定義
interface Announce {
  id: string;
  title: string;
  content: string;
  thumbnail_image: AnnounceMedia | null;
  gallery_images: AnnounceMedia[] | null;
  videos: AnnounceMedia[] | null;
  publish_date: string;
  display_priority: number;
  is_published: boolean;
}

// メディアURLを生成（/static/ も R2 も透過的に扱う）
const getMediaUrl = (storagePath: string): string => resolveMediaUrl(storagePath);

// お知らせのサムネイルURLを取得（画像優先、なければ動画のサムネイル）
const getAnnounceThumbnail = (announce: Announce): string | null => {
  // 1. サムネイル画像があればそれを使用
  if (announce.thumbnail_image?.storage_path) {
    return getMediaUrl(announce.thumbnail_image.storage_path);
  }
  // 2. ギャラリー画像があれば最初の1枚を使用
  if (announce.gallery_images && announce.gallery_images.length > 0 && announce.gallery_images[0].storage_path) {
    return getMediaUrl(announce.gallery_images[0].storage_path);
  }
  // 3. 動画があればその情報を返す（動画アイコン表示用）
  return null;
};

// 動画があるかチェック
const hasVideo = (announce: Announce): boolean => {
  return !!(announce.videos && announce.videos.length > 0);
};

// ヒーロースライダーの画像。完成した施工物を主役に置き、最後に施工中のカットを1枚添える。
// 個人宅（○○様邸）の完成写真は載せない
const heroImages = [
  {
    url: "/static/TOP%20%E5%86%99%E7%9C%9F/IMG_E2067.JPG",
    alt: "都建設株式会社 施工した明石寺もみじ谷霊園の完成した区画",
  },
  {
    url: "/static/TOP%20%E5%86%99%E7%9C%9F/DJI_0460.JPG",
    alt: "都建設株式会社 造成が完成した霊園の全景",
  },
  {
    url: "/static/TOP%20%E5%86%99%E7%9C%9F/IMG_4346.JPG",
    alt: "都建設株式会社 完成した林道",
  },
  {
    url: "/static/%E8%A7%A3%E4%BD%93%E5%B7%A5%E4%BA%8B/%E6%97%A7%E6%B6%88%E9%98%B2%E7%BD%B2%E8%A7%A3%E4%BD%93%E5%B7%A5%E4%BA%8B/IMG_4179.JPG",
    alt: "都建設株式会社 旧消防署の鉄骨を解体する自社重機",
  },
];


// サービス一覧
const services = [
  {
    title: "業務内容",
    description: "解体工事、ガーデニング工事、土木工事などを行なっております。",
    image: "/static/%E8%A7%A3%E4%BD%93%E5%B7%A5%E4%BA%8B/%E6%9C%A8%E9%80%A0%E5%AE%B6%E5%B1%8B%E8%A7%A3%E4%BD%93%E5%B7%A5%E4%BA%8B/DJI_0004.JPG",
    link: "/services",
    alt: "業務内容 - 解体工事、ガーデニング工事、土木工事",
  },
  {
    title: "施工実績",
    description: "数多くの施工実績がございます。ぜひご覧ください。",
    // 個人宅の写真は使わないので、霊園造成の完成カットを充てる
    image: "/static/%E9%80%A0%E6%88%90%E5%B7%A5%E4%BA%8B/%E5%A2%93%E5%9C%B0%E9%80%A0%E6%88%90%E5%B7%A5%E4%BA%8B/IMG_4712.JPG",
    link: "/works",
    alt: "施工実績 - 過去の施工事例",
  },
  {
    title: "採用情報",
    description: "一緒に働く仲間を募集しています！",
    image: "/static/%E7%A4%BE%E5%86%85%E9%96%A2%E4%BF%82/%E6%9C%9D%E7%A4%BC%E5%A0%B4%E6%89%80.JPG",
    link: "/recruit",
    alt: "採用情報 - スタッフ募集",
  },
];

export const HomePage = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [resultCategories, setResultCategories] = useState<ResultCategory[]>([]);
  const [results, setResults] = useState<Result[]>([]);
  const [announces, setAnnounces] = useState<Announce[]>([]);
  const [isLinePopupOpen, setIsLinePopupOpen] = useState(false);
  const pathPrefix = getPathPrefix();

  const LINE_URL = "https://line.me/R/ti/p/@703jwzlx?from=page&accountId=703jwzlx";

  const isMobileDevice = useCallback(() => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
  }, []);

  const handleLineClick = useCallback(() => {
    // モバイル端末は直接LINEに遷移、PCはポップアップ表示
    if (isMobileDevice()) {
      window.open(LINE_URL, "_blank", "noopener,noreferrer");
    } else {
      setIsLinePopupOpen(true);
    }
  }, [isMobileDevice]);

  const closeLinePopup = useCallback(() => {
    setIsLinePopupOpen(false);
  }, []);

  // 施工実績データを取得
  useEffect(() => {
    const fetchResults = async () => {
      try {
        const response = await authFetch(`${getApiBase()}/results`);
        if (response.ok) {
          const data = await response.json();
          setResultCategories(data.categories || []);
          setResults(data.results || []);
        }
      } catch (error) {
        console.error("施工実績の取得に失敗しました:", error);
      }
    };
    fetchResults();
  }, []);

  // お知らせデータを取得（最新5件）
  useEffect(() => {
    const fetchAnnounces = async () => {
      try {
        const response = await authFetch(`${getApiBase()}/announces?limit=5`);
        if (response.ok) {
          const data = await response.json();
          setAnnounces(data.announces || []);
        }
      } catch (error) {
        console.error("お知らせの取得に失敗しました:", error);
      }
    };
    fetchAnnounces();
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <>
      <SEOHead page={STATIC_PAGES.home} />

      <div className="public-page public-home">
        <PublicHeader activePage="home" />

        {/* メインコンテンツ */}
        <main id="main-content" role="main">
          {/* ヒーローセクション */}
          <section className="hero-section" aria-label="メインビジュアル">
            <div className="hero-slider">
              {heroImages.map((image, index) => (
                <div
                  key={index}
                  className={`hero-slide ${index === currentSlide ? "active" : ""}`}
                  style={{
                    backgroundImage: `url(${image.url})`,
                  }}
                  role="img"
                  aria-label={image.alt}
                  aria-hidden={index !== currentSlide}
                />
              ))}
              <div className="hero-overlay" aria-hidden="true" />
              <div className="hero-content">
                {/* 社名を上、キャッチコピーを下に置く */}
                <h1 className="hero-title">都建設株式会社</h1>
                <p className="hero-tagline">人々の生活を安全で快適に</p>
              </div>
            </div>
          </section>

          {/* 会社概要セクション - article要素でセマンティック */}
          <section className="about-section" aria-labelledby="about-heading">
            <h2 id="about-heading" className="section-title">『安心安全な街づくり』</h2>
            <div className="about-content">
              <p>都建設株式会社は愛媛県西予市を拠点に</p>
              <p>
                <strong>解体工事</strong>、<strong>住宅等造成工事</strong>、
                <strong>土木工事</strong>、<strong>ガーデニング工事</strong>
                などを展開しています。
              </p>
              <p>お客様のご要望をお聞きし、最善の施工方法までご提案することで</p>
              <p>より長く、より安心できる生活の基盤づくりに努めております。</p>
            </div>
            <a href="/about" className="about-button">
              会社概要はこちら
            </a>
          </section>

          {/* 選ばれる理由セクション */}
          <section className="reasons-section" aria-labelledby="reasons-heading">
            <h2 id="reasons-heading" className="section-title reasons-title">
              都建設株式会社が選ばれる理由
            </h2>

            <article className="reason-item">
              <figure className="reason-image">
                <img
                  src="/static/%E7%A4%BE%E5%86%85%E9%96%A2%E4%BF%82/%E8%87%AA%E7%A4%BE%E6%96%BD%E5%B7%A5.JPG"
                  alt="自社保有の重機が並ぶ作業場"
                  className="reason-image-img"
                  loading="lazy"
                />
              </figure>
              <div className="reason-content">
                <h3>受注した工事の殆どを自社にて施工</h3>
                <p>
                  受注した工事の殆どを自社にて施工する為、不必要な経費、無駄な費用を
                  省く事ができます。またお客様のご要望をお聞きし、ご希望の施工方法を
                  ご提案致します。
                </p>
                <p>
                  <strong>信頼と品質</strong>を保ちながら無駄のない施工を心がけます。
                </p>
              </div>
            </article>

            <article className="reason-item reverse">
              <div className="reason-content">
                <h3>最適な料金のご提案</h3>
                <p>
                  施工の前に十分なヒアリングの時間を設けています。お客様に合わせて料
                  金体系をカスタマイズし、<strong>最適なご提案</strong>
                  をさせていただきます。
                </p>
              </div>
              <figure className="reason-image">
                <img
                  src="/static/%E7%A4%BE%E5%86%85%E9%96%A2%E4%BF%82/%E6%89%93%E3%81%A1%E5%90%88%E3%82%8F%E3%81%9B%E5%A0%B4%E6%89%80.JPG"
                  alt="丁寧なヒアリングと料金提案"
                  className="reason-image-img"
                  loading="lazy"
                />
              </figure>
            </article>
          </section>

          {/* お知らせセクション */}
          <section className="news-section" aria-labelledby="news-heading">
            <h2 id="news-heading" className="section-title">お知らせ</h2>
            <div className="news-list" role="feed" aria-label="お知らせ一覧">
              {announces.length > 0 ? (
                announces.map((announce, index) => {
                  const publishDate = new Date(announce.publish_date);
                  const dateDisplay = publishDate.toLocaleDateString("ja-JP", {
                    year: "numeric",
                    month: "numeric",
                    day: "numeric",
                  });
                  const dateISO = publishDate.toISOString().split("T")[0];
                  // 内容を50文字で切り詰めて抜粋を作成
                  const excerpt = announce.content.length > 50
                    ? announce.content.substring(0, 50) + "...続きを読む"
                    : announce.content;

                  const thumbnail = getAnnounceThumbnail(announce);
                  const showVideoIcon = hasVideo(announce);

                  return (
                    <article key={announce.id} className="news-item" aria-labelledby={`news-title-${index}`}>
                      <Link to={`${pathPrefix}/news/${announce.id}`} className="news-item-link">
                        {/* サムネイル表示エリア */}
                        <div className="news-thumbnail">
                          {thumbnail ? (
                            <img
                              src={thumbnail}
                              alt={announce.title}
                              className="news-thumbnail-img"
                              loading="lazy"
                            />
                          ) : (
                            <div className="news-thumbnail-placeholder">
                              {showVideoIcon ? (
                                <span className="news-video-icon" aria-label="動画あり">▶</span>
                              ) : (
                                <span className="news-no-image">📢</span>
                              )}
                            </div>
                          )}
                          {/* 動画バッジ（サムネイルがあっても動画がある場合は表示） */}
                          {thumbnail && showVideoIcon && (
                            <span className="news-video-badge" aria-label="動画あり">▶ 動画</span>
                          )}
                        </div>
                        {/* テキストコンテンツ */}
                        <div className="news-content">
                          <h3 id={`news-title-${index}`} className="news-title">
                            {announce.title}
                          </h3>
                          <time className="news-date" dateTime={dateISO}>
                            {dateDisplay}
                          </time>
                          <p className="news-excerpt">{excerpt}</p>
                        </div>
                      </Link>
                    </article>
                  );
                })
              ) : (
                <p className="news-empty">お知らせはありません</p>
              )}
            </div>
            <Link to={`${pathPrefix}/news`} className="news-button">
              記事一覧を見る
            </Link>
          </section>

          {/* 施工実績セクション */}
          <section className="results-section" aria-labelledby="results-heading">
            <h2 id="results-heading" className="section-title">施工実績</h2>
            <p className="results-intro">
              企業の本社や工場などの大規模建築、店舗や学校などの公共施設を含む
              幅広い施工実績がございます。
            </p>
            <div className="results-grid">
              {(() => {
                // 中身（公開施工実績）が 1 件以上あるカテゴリだけ表示する
                const visibleCategories = resultCategories.filter((c) =>
                  results.some((r) => r.category_id === c.id)
                );
                return visibleCategories.length > 0;
              })() ? (
                resultCategories
                  .filter((c) => results.some((r) => r.category_id === c.id))
                  .map((category) => {
                    const categoryResults = results.filter(
                      (r) => r.category_id === category.id
                    );
                    const latestResult = categoryResults[0];
                    return (
                      <a
                        key={category.id}
                        href={`/works?category=${category.id}`}
                        className="result-category-card"
                      >
                        <div className="result-category-image">
                          {latestResult?.main_image?.storage_path ? (
                            <img
                              src={resolveMediaUrl(latestResult.main_image.storage_path)}
                              alt={category.name}
                            />
                          ) : (
                            <div
                              className="placeholder-image"
                              data-text={category.name}
                              role="img"
                              aria-label={category.name}
                            />
                          )}
                        </div>
                        <div className="result-category-name">{category.name}</div>
                      </a>
                    );
                  })
              ) : (
                // フォールバック: ダミーカテゴリ表示
                <>
                  {[
                    "解体工事",
                    "土木工事",
                    "ガーデニング工事",
                    "造成工事",
                  ].map((name, index) => (
                    <a
                      key={index}
                      href={`/works`}
                      className="result-category-card"
                    >
                      <div className="result-category-image">
                        <div
                          className="placeholder-image"
                          data-text={name}
                          role="img"
                          aria-label={name}
                        />
                      </div>
                      <div className="result-category-name">{name}</div>
                    </a>
                  ))}
                </>
              )}
            </div>
            <a href="/works" className="results-button">
              施工実績一覧を見る
            </a>
          </section>

          {/* サービスカードセクション */}
          <section className="cards-section" aria-labelledby="services-heading">
            <h2 id="services-heading" className="visually-hidden">
              サービス紹介
            </h2>
            <div className="cards-grid">
              {services.map((service, index) => (
                <article key={index} className="card" aria-labelledby={`service-title-${index}`}>
                  <figure className="card-image">
                    <img
                      src={service.image}
                      alt={service.alt}
                      className="card-image-img"
                      loading="lazy"
                    />
                  </figure>
                  <div className="card-content">
                    <h3 id={`service-title-${index}`}>{service.title}</h3>
                    <p>{service.description}</p>
                    <a href={service.link} className="card-button">
                      詳細はこちら
                      <span className="visually-hidden">: {service.title}</span>
                    </a>
                  </div>
                </article>
              ))}
            </div>
          </section>

          {/* LINE CTAセクション */}
          <section className="line-section" aria-labelledby="contact-cta-heading">
            <h2 id="contact-cta-heading" className="visually-hidden">
              お問い合わせ方法
            </h2>
            <p className="line-intro">
              ご予約は下記の公式LINE、または問い合わせフォームよりご連絡ください。
            </p>
            <div className="line-box">
              <p className="line-box-title">公式LINEで友だち追加</p>
              <p className="line-account-name">LINE公式アカウント名：都建設株式会社</p>
              <div className="line-content">
                <button
                  type="button"
                  className="line-cta-button"
                  data-track="line"
                  onClick={handleLineClick}
                >
                  <svg viewBox="0 0 48 48" width="40" height="40" aria-hidden="true">
                    <rect width="48" height="48" rx="10" fill="#06C755" />
                    <path
                      d="M24 8c-8.84 0-16 5.82-16 12.99 0 6.43 5.7 11.82 13.4 12.84.52.11 1.23.35 1.41.79.16.41.1 1.04.05 1.45l-.23 1.37c-.07.41-.32 1.6 1.4.87 1.72-.73 9.27-5.46 12.65-9.35C39 26.44 40 23.96 40 20.99 40 13.82 32.84 8 24 8z"
                      fill="white"
                    />
                    <text
                      x="24"
                      y="23"
                      fill="#06C755"
                      fontSize="9"
                      fontWeight="900"
                      fontFamily="Arial Black, Arial, sans-serif"
                      textAnchor="middle"
                      dominantBaseline="middle"
                      letterSpacing="-0.5"
                    >
                      LINE
                    </text>
                  </svg>
                  <span>友だち追加</span>
                  <span className="arrow" aria-hidden="true">›</span>
                </button>
              </div>
            </div>
          </section>

          {/* マップセクション */}
          <section className="map-section" aria-labelledby="map-heading">
            <h2 id="map-heading" className="visually-hidden">
              アクセス
            </h2>
            <address className="map-info">
              <p className="map-address">
                〒797-0015 愛媛県西予市宇和町卯之町1丁目432
              </p>
              <a
                href="https://www.google.com/maps/dir/?api=1&destination=愛媛県西予市宇和町卯之町1丁目432"
                className="map-route"
                target="_blank"
                rel="noopener noreferrer"
              >
                ルート
              </a>
              <a
                href="https://maps.google.com/?q=愛媛県西予市宇和町卯之町1丁目432"
                className="map-expand"
                target="_blank"
                rel="noopener noreferrer"
              >
                拡大地図を表示
              </a>
            </address>
            <div className="map-embed">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3323.1!2d132.5!3d33.4!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzPCsDI0JzAwLjAiTiAxMzLCsDMwJzAwLjAiRQ!5e0!3m2!1sja!2sjp!4v1234567890"
                width="100%"
                height="300"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="都建設株式会社 所在地マップ - 愛媛県西予市宇和町卯之町1丁目432"
              />
            </div>
          </section>
        </main>

        {/* フッター */}
        <footer className="public-footer" role="contentinfo">
          <div className="footer-content">
            <div className="footer-logo">
              <img
                src="/static/miyako-logo-small-white.png"
                srcSet="/static/miyako-logo-small-white.png 1x, /static/miyako-logo-2x-white.png 2x"
                alt="都建設株式会社"
                className="footer-logo-image"
                width={60}
                height={45}
              />
              <span className="footer-company-name">都建設株式会社</span>
            </div>

            <address className="footer-address-block">
              <a
                href="https://maps.google.com/?q=愛媛県西予市宇和町卯之町1丁目432"
                className="footer-address"
                target="_blank"
                rel="noopener noreferrer"
              >
                〒797-0015 愛媛県西予市宇和町卯之町1丁目432
              </a>

              <a href="tel:0894-62-4511" className="footer-phone">
                お電話：<span itemProp="telephone">0894-62-4511</span>
              </a>
            </address>

            <nav className="footer-nav" aria-label="フッターナビゲーション">
              <a href="/">ホーム</a>
              <a href="/about">会社概要</a>
              <a href="/services">業務内容</a>
              <a href="/works">施工実績</a>
              <a href="/recruit">採用情報</a>
              <a href="/contact">お問い合わせ</a>
            </nav>

            <p className="footer-copyright">
              <small>©2026 都建設株式会社</small>
            </p>
          </div>
        </footer>

        {/* LINE公式アカウント ポップアップ */}
        {isLinePopupOpen && (
          <>
            <div className="line-popup-overlay" onClick={closeLinePopup} aria-hidden="true" />
            <div className="line-popup" role="dialog" aria-modal="true" aria-labelledby="line-popup-title">
              <button
                className="line-popup-close"
                onClick={closeLinePopup}
                aria-label="閉じる"
                type="button"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="6" y1="6" x2="18" y2="18" />
                  <line x1="6" y1="18" x2="18" y2="6" />
                </svg>
              </button>
              <div className="line-popup-content">
                <h3 id="line-popup-title" className="line-popup-title">LINE公式アカウント</h3>
                <p className="line-popup-description">
                  お気軽にお問い合わせください。<br />
                  QRコードを読み取るか、下のボタンから友だち追加できます。
                </p>
                <div className="line-popup-qr">
                  <img
                    src="https://qr-official.line.me/gs/M_703jwzlx_GW.png"
                    alt="LINE公式アカウント QRコード"
                    width="180"
                    height="180"
                  />
                </div>
                <a
                  href="https://line.me/R/ti/p/@703jwzlx?from=page&accountId=703jwzlx"
                  className="line-popup-button"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                    <path
                      d="M12 2C6.48 2 2 5.82 2 10.49c0 4.21 3.74 7.74 8.79 8.41.34.07.8.23.92.52.1.27.07.68.03.95l-.15.89c-.05.27-.21 1.05.92.57 1.13-.48 6.09-3.58 8.3-6.14C22.21 13.91 22 12.29 22 10.49 22 5.82 17.52 2 12 2z"
                      fill="white"
                    />
                  </svg>
                  友だち追加
                </a>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default HomePage;
